import clsx from "clsx";
import { Field, Form, Formik } from "formik";
import { TextField, RadioGroup } from "formik-material-ui";
import { Link } from "gatsby";
import { navigate } from "gatsby-link";
import { Button } from "gatsby-theme-material-ui";
import PropTypes from "prop-types";
import React, { ReactElement, useRef, useState } from "react";
import { graphql } from "gatsby";
import DateFnsUtils from "@date-io/dayjs";
import {
  Container,
  LinearProgress,
  Theme,
  FormControlLabel,
  Radio,
  Typography,
  Checkbox,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import Content, { HTMLContent } from "../components/Content";
import Layout from "../components/Layout";
import Helmet from "react-helmet";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gridArea: "content",
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
      [theme.breakpoints.up("sm")]: {
        alignContent: "center",
        alignItems: "stretch",
        display: "grid",
        gap: `${theme.spacing(8)}px`,
        gridAutoFlow: "column",
        gridTemplateColumns: "2fr 2fr",
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2),
      },
    },
    label: {
      '& a ' : {
        textDecoration: "underline",
        fontWeight: 500,
      }
    },
    hiddenForm: {
      display:"none"
    },
    form: {
      display: "grid",
      gap: `${theme.spacing(2)}px`,
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
      '& .MuiFormControlLabel-labelPlacementStart' : {
        flexDirection: "row",
        marginLeft: 0,
      }
    },
    overview: {
      alignContent: "center",
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.secondary.contrastText,
      display: "grid",
      padding: theme.spacing(4),
    },
    radioGroup: {
      display: "flex",
      flexDirection: "column",
      '& .MuiFormControlLabel-labelPlacementStart' : {
        flexDirection: "row",
        marginLeft: 0,
      }
    },
    title: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
    previewImage: {
      width:"100%",
    },
    infoWrapper: {
      display:"flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "12px",
      background: "white",
    },
    textInfo: {
      fontSize: "14px",
      display: "block",
      color: "black",
      width: "100%",
      marginBottom: "8px",
    },
    description: {},
  })
);

function encode(data): string {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

export const BookValTemplate = ({
  formTitle,
  content,
  contentComponent,
  location
}): ReactElement => {
  const classes = useStyles();
  const PageContent = contentComponent || Content;
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [valuation, setValuation] = useState("SALE")
  const [checked, setChecked] = useState(false)
  console.log(formRef.current)
  const handleChange = e => {
    const { name, value } = e.target;
    setValuation(value)
    
  };
  const handleCheckChange = (event) => {
    setChecked(!checked)
  };
  const label = (
    <p className={classes.label}>
      I have read and agree with Williams Lynch <Link to="/privacy-policy">GPDR Policy*</Link>
    </p>
  )
  return (
    <Container className={clsx(classes.root)} component="main" fluid="fluid">
     
      <section className={clsx(classes.segment, classes.overview)}>
        <PageContent className="content" content={content} />
      </section>
      <section className={clsx(classes.segment)}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Formik
            initialValues={{
              email: "",
              first_name: "",
              last_name: "",
              post_code: "",
              registrant: "CLIENT",
              valuation: "SALE",
              gpdr: checked
            }}
            
            validate={(values): Partial => {
              const errors: Partial<Values> = {};
              if (!values.first_name) {
                errors.first_name = "Required"
              }
              if (!values.last_name) {
                errors.last_name = "Required"
              }
              if (!values.post_code) {
                errors.post_code = "Required"
              }
              
              if (!values.email) {
                errors.email = "Required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
              ) {
                errors.email = "Invalid email address";
              }
              
              return errors;
            }}
            onSubmit={(values): void => {
              const form = formRef.current;
                
                setIsSubmitting(true);

                fetch(form.getAttribute("action"), {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                  body: encode({
                    "form-name": form.getAttribute("name"),
                    'subject': "ONLINE ENQUIRY – Book valuation",
                    ...values,
                  }),
                })
                  .then(() => setIsSubmitting(false))
                  .then(() => navigate(form.getAttribute("action")))
                  .catch((error) => alert(error));
            }}
            
          >
            {({ submitForm, isSubmitting }): ReactElement => (
              <Form
                action="/book-valuation/thanks/"
                className={clsx(classes.form)}
                data-netlify-honeypot="bot-field"
                data-netlify="true"
                method="post"
                name={`book-valuation-${valuation === "RENT" ? "RENT" : "SALE"}`}
                ref={formRef}
              >
                {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
                <input type="hidden" name="form-name" value="contact" />
                <input type="hidden" id="subject" name="subject" value="ONLINE ENQUIRY – Book valuation" />
                <Typography component="legend" variant="h5">
                  {formTitle}
                </Typography>
                <Field
                  component={RadioGroup}
                  className={classes.radioGroup}
                  name="valuation"
                  value={valuation}
                  row
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="For sale"
                    labelPlacement="start"
                    value="SALE"
                    style={{ marginLeft: 0 }}
                    onChange={handleChange}
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="For rent"
                    labelPlacement="start"
                    value="RENT"
                    onChange={handleChange}
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Not sure"
                    labelPlacement="start"
                    value="NOTSURE"
                    onChange={handleChange}
                  />
                </Field>
                <Field
                  component={TextField}
                  label="First Name"
                  name="first_name"
                  type="name"
                  required
                />

                <Field
                  component={TextField}
                  label="Last Name"
                  name="last_name"
                  type="name"
                  required
                />

                <Field
                  component={TextField}
                  label="Telephone"
                  name="telephone"
                  type="tel"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                />

                <Field
                  component={TextField}
                  label="Email"
                  name="email"
                  type="email"
                  required
                />

                <Field
                  component={TextField}
                  label="Number of bedrooms"
                  name="Bedrooms"
                  type="number"
                />

                <Field
                  component={TextField}
                  label="Property Address"
                  name="address"
                  type="text"
                />

                <Field
                  component={TextField}
                  label="Property Post Code"
                  name="post_code"
                  type="text"
                  required
                />
                
                {
                  valuation === "RENT" && (
                      <>
                          <Field
                              component={TextField}
                              label="Rental Duration"
                              name="rental_duration"
                              type="text"
                  
                          />
                          <Field
                              component={TextField}
                              label="Date Property Available"
                              name="date_property_available"
                              type="text"
                          />
                      </>
                  )
                }
                {
                  valuation !== "RENT" && (
                      <Field
                          component={TextField}
                          label="Type of Property"
                          name="type_of_property"
                          type="text"
                      />
                  )
                }
                <FormControlLabel
                  value={checked}
                  control={
                    <Checkbox
                      name="GPDR"
                      checked={checked}
                      onChange={handleCheckChange}
                      color="secondary"
                    />
                  }
                  labelPlacement="end"
                  label={label}
                />
                
                {isSubmitting && <LinearProgress />}

                <Button
                  variant="contained"
                  disabled={isSubmitting || checked !== true}
                  onClick={submitForm}
                >
                  Send
                </Button>
              </Form>
            )}
          </Formik>
         
        </MuiPickersUtilsProvider>
       
      </section>
      <Formik>
        <Form
          action="/book-valuation/thanks/"
          className={clsx(classes.hiddenForm)}
          data-netlify-honeypot="bot-field"
          data-netlify="true"
          method="post"
          name="book-valuation-RENT"
        >
          {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
          <input type="hidden" name="form-name" value="contact" />
          <input type="hidden" id="subject" name="subject" value="ONLINE ENQUIRY – Book valuation" />
        </Form>
      </Formik>
      
    </Container>
  );
};

BookValTemplate.propTypes = {
  title: PropTypes.string,
  heading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string,
  subheading: PropTypes.string,
  description: PropTypes.string,
  formTitle: PropTypes.string.isRequired,
  content: PropTypes.string,
  contentComponent: PropTypes.func,
  helmet: PropTypes.object
};

const BookVal = ({ data, location }): ReactElement => {
  const { markdownRemark: post } = data;
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <BookValTemplate
        title={post.frontmatter.title}
        heading={post.frontmatter.heading}
        subheading={post.frontmatter.subheading}
        description={post.frontmatter.description}
        featuredimage={post.frontmatter.featuredimage}
        imagewidth={post.frontmatter.imagewidth}
        contentComponent={HTMLContent}
        formTitle={post.frontmatter.formTitle}
        content={post.html}
        location={location}
        helmet={
          <Helmet titleTemplate="%s">
            <title>{`${post.frontmatter.title}`}</title>
            <meta name="description" content={`${post.frontmatter.description}`} />
            <meta name="image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="og:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="twitter:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
          </Helmet>
        }
      />
    </Layout>
  );
};

BookVal.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object,
  formTitle: PropTypes.string.isRequired,
};

export default BookVal;

export const pageQuery = graphql`
  query BookVal($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        heading
        subheading
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        description
        formTitle
      }
    }
  }
`;


